/**
 * NutritionSummaryCard Component
 * Reusable card for displaying nutrition summaries from various sources
 */

import React from "react";
import { LLMMacros } from "../types/common";

interface NutritionData {
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
    fiber?: number;
}

interface NutritionSummaryCardProps {
    title?: string;
    nutritionData: NutritionData | LLMMacros;
    itemCount?: number;
    itemLabel?: string;
    variant?: "compact" | "standard" | "detailed";
    className?: string;
    style?: React.CSSProperties;
}

export const NutritionSummaryCard: React.FC<NutritionSummaryCardProps> = ({
    title = "Nutrition Summary",
    nutritionData,
    itemCount,
    itemLabel = "items",
    variant = "standard",
    className,
    style,
}) => {
    const calories = nutritionData.calories || 0;
    const protein = nutritionData.protein || 0;
    const carbs = nutritionData.carbs || 0;
    const fat = nutritionData.fat || 0;
    const fiber = nutritionData.fiber || 0;

    // Base styles
    const baseCardStyle: React.CSSProperties = {
        backgroundColor: "#f7fafc",
        borderRadius: "12px",
        border: "2px solid #e2e8f0",
        ...style,
    };

    const compactCardStyle: React.CSSProperties = {
        ...baseCardStyle,
        padding: "12px 16px",
    };

    const standardCardStyle: React.CSSProperties = {
        ...baseCardStyle,
        padding: "20px",
    };

    const detailedCardStyle: React.CSSProperties = {
        ...baseCardStyle,
        padding: "24px",
    };

    const titleStyle: React.CSSProperties = {
        fontSize:
            variant === "compact"
                ? "14px"
                : variant === "detailed"
                ? "20px"
                : "18px",
        fontWeight: "600",
        color: "#1a202c",
        marginBottom: variant === "compact" ? "8px" : "12px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    };

    const itemCountStyle: React.CSSProperties = {
        fontSize: variant === "compact" ? "12px" : "14px",
        color: "#718096",
        fontWeight: "normal",
    };

    const statsGridStyle: React.CSSProperties = {
        display: "grid",
        gridTemplateColumns:
            variant === "compact"
                ? "repeat(4, 1fr)"
                : variant === "detailed"
                ? "repeat(auto-fit, minmax(140px, 1fr))"
                : "repeat(auto-fit, minmax(120px, 1fr))",
        gap: variant === "compact" ? "8px" : "16px",
    };

    const statItemStyle: React.CSSProperties = {
        textAlign: "center",
    };

    const statValueStyle: React.CSSProperties = {
        fontSize:
            variant === "compact"
                ? "16px"
                : variant === "detailed"
                ? "28px"
                : "24px",
        fontWeight: "bold",
        color: "#2d3748",
    };

    const statLabelStyle: React.CSSProperties = {
        fontSize: variant === "compact" ? "10px" : "12px",
        color: "#718096",
        textTransform: "uppercase",
        fontWeight: "500",
    };

    const cardStyle =
        variant === "compact"
            ? compactCardStyle
            : variant === "detailed"
            ? detailedCardStyle
            : standardCardStyle;

    return (
        <div className={className} style={cardStyle}>
            <div style={titleStyle}>
                <span>{title}</span>
                {itemCount !== undefined && (
                    <span style={itemCountStyle}>
                        {itemCount} {itemLabel}
                    </span>
                )}
            </div>

            <div style={statsGridStyle}>
                <div style={statItemStyle}>
                    <div style={statValueStyle}>{Math.round(calories)}</div>
                    <div style={statLabelStyle}>Calories</div>
                </div>

                <div style={statItemStyle}>
                    <div style={statValueStyle}>{Math.round(protein)}g</div>
                    <div style={statLabelStyle}>Protein</div>
                </div>

                <div style={statItemStyle}>
                    <div style={statValueStyle}>{Math.round(carbs)}g</div>
                    <div style={statLabelStyle}>Carbs</div>
                </div>

                <div style={statItemStyle}>
                    <div style={statValueStyle}>{Math.round(fat)}g</div>
                    <div style={statLabelStyle}>Fat</div>
                </div>

                {variant === "detailed" && (
                    <div style={statItemStyle}>
                        <div style={statValueStyle}>{Math.round(fiber)}g</div>
                        <div style={statLabelStyle}>Fiber</div>
                    </div>
                )}
            </div>
        </div>
    );
};
